import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import useTranslation from '../../hooks/useTranslation';
import { useAuth } from '../../auth/AuthProvider';
import { projectService } from '../../services/projectService';
import { canEditProject, canEditAllProjects } from '../../utils/permissions';
import StarRating from '../../components/ui/StarRating';
import { TRADES, PROFESSIONS } from '../../constants/directoryOptions';
import {
  listSubcontractorsForProject,
  listProfessionalsForProject,
  listClientsForProject,
  attachSubcontractor,
  attachProfessional,
  attachClient,
  detachEntry,
  updateEntry,
  listAvailableSubcontractors,
  listAvailableProfessionals,
  listAvailableClients,
  fetchTabCounts,
} from '../../services/projectDirectoryService';

// ─── Permission helper ─────────────────────────────────────────────────────────
function canEditProjectDirectory(project, userProfile) {
  // Grants edit access to:
  //   • admin, accountant, delegates (can_view_all_projects=true) — via canEditAllProjects
  //   • project PM, assistant, supervisor, coordinator — via canEditProject membership check
  return canEditAllProjects(userProfile) || canEditProject(project, userProfile);
}

// ─── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => (
  <div
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}
  >
    <Icon name={type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-70">
      <Icon name="X" size={14} />
    </button>
  </div>
);

// ─── Sort Icon — identical to company-wide directory ──────────────────────────
const SortIcon = ({ columnKey, sortConfig }) => {
  if (sortConfig?.key !== columnKey) {
    return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
  }
  return sortConfig?.direction === 'asc' ? (
    <Icon name="ChevronUp" size={16} className="text-primary" />
  ) : (
    <Icon name="ChevronDown" size={16} className="text-primary" />
  );
};

// ─── Attach Modal ──────────────────────────────────────────────────────────────
const AttachModal = ({ activeTab, projectId, onClose, onSaved, t }) => {
  const [available, setAvailable] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchAvailable = async () => {
      setLoadingAvailable(true);
      let data = [];
      if (activeTab === 'subcontractor') data = await listAvailableSubcontractors(projectId);
      else if (activeTab === 'professional') data = await listAvailableProfessionals(projectId);
      else data = await listAvailableClients(projectId);
      setAvailable(data || []);
      setLoadingAvailable(false);
    };
    fetchAvailable();
  }, [activeTab, projectId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getModalTitle = () => {
    if (activeTab === 'subcontractor') return t('projectDirectory.modals.attachSubcontractor.title');
    if (activeTab === 'client') return t('projectDirectory.modals.attachClient.title');
    return t('projectDirectory.modals.attachProfessional.title');
  };

  const getEntryLabel = (entry) => {
    if (!entry) return '';
    const suffix = entry?.trade ? ` — ${entry?.trade}` : entry?.profession ? ` — ${entry?.profession}` : '';
    const contact = entry?.contact_person ? ` (${entry?.contact_person})` : '';
    return `${entry?.company_name}${suffix}${contact}`;
  };

  const filteredAvailable = available?.filter((e) =>
    getEntryLabel(e)?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const selectedEntry = available?.find((e) => e?.id === selectedId);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!selectedId) { setError(t('projectDirectory.modals.errorSelectEntry')); return; }
    setSaving(true);
    setError('');
    let result;
    if (activeTab === 'subcontractor') {
      result = await attachSubcontractor({ projectId, subcontractorId: selectedId, role: '', notes: null });
    } else if (activeTab === 'professional') {
      result = await attachProfessional({ projectId, professionalId: selectedId, role: '', notes: null });
    } else {
      result = await attachClient({ projectId, clientId: selectedId, role: '', notes: null });
    }
    setSaving(false);
    if (result?.error) {
      const isDuplicate = result?.error?.code === '23505';
      setError(isDuplicate ? t('projectDirectory.modals.errorDuplicate') : (result?.error?.message || t('projectDirectory.modals.errorGeneric')));
    } else {
      onSaved();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{getModalTitle()}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projectDirectory.modals.attachField.label')} <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <div
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm cursor-pointer flex items-center justify-between bg-white hover:border-gray-400 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500"
                onClick={() => setShowDropdown(true)}
              >
                <span className={selectedEntry ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedEntry ? getEntryLabel(selectedEntry) : t('projectDirectory.modals.attachField.placeholder')}
                </span>
                <Icon name="ChevronDown" size={16} className="text-gray-400 flex-shrink-0" />
              </div>
              {showDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      autoFocus
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      placeholder={t('projectDirectory.searchPlaceholder')}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="overflow-y-auto max-h-44">
                    {loadingAvailable ? (
                      <div className="px-3 py-4 text-sm text-gray-500 text-center">{t('common.loading')}</div>
                    ) : filteredAvailable?.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-gray-500 text-center">{t('projectDirectory.modals.noAvailable')}</div>
                    ) : (
                      filteredAvailable?.map((entry) => (
                        <button
                          key={entry?.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 transition-colors"
                          onClick={() => { setSelectedId(entry?.id); setShowDropdown(false); setSearchTerm(''); }}
                        >
                          {getEntryLabel(entry)}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <Icon name="AlertCircle" size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('projectDirectory.buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving && <Icon name="Loader2" size={14} className="animate-spin" />}
              {saving ? t('common.saving') : t('projectDirectory.buttons.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Modal ────────────────────────────────────────────────────────────────
const EditModal = ({ entry, activeTab, onClose, onSaved, t }) => {
  const [role, setRole] = useState(entry?.role || '');
  const [notes, setNotes] = useState(entry?.notes || '');
  const [active, setActive] = useState(entry?.active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    let result = await updateEntry(activeTab, entry?.projectEntryId, {
      role: role?.trim() || '',
      notes: notes?.trim() || null,
      active,
    });
    setSaving(false);
    if (result?.error) {
      setError(result?.error?.message || t('projectDirectory.modals.errorGeneric'));
    } else {
      onSaved();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('projectDirectory.modals.editTitle')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{entry?.company_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projectDirectory.modals.notes.label')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActive(!active)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${active ? 'bg-primary' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${active ? 'translate-x-4' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-gray-700">{t('projectDirectory.modals.activeLabel')}</span>
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <Icon name="AlertCircle" size={14} />
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              {t('projectDirectory.buttons.cancel')}
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors flex items-center gap-2">
              {saving && <Icon name="Loader2" size={14} className="animate-spin" />}
              {saving ? t('common.saving') : t('projectDirectory.buttons.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Detach Confirm Modal ──────────────────────────────────────────────────────
const DetachModal = ({ entry, activeTab, onClose, onConfirmed, t }) => {
  const [detaching, setDetaching] = useState(false);

  const handleConfirm = async () => {
    setDetaching(true);
    await detachEntry(activeTab, entry?.projectEntryId);
    setDetaching(false);
    onConfirmed();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Icon name="UserMinus" size={20} className="text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{t('projectDirectory.modals.detachConfirm.title')}</h2>
          </div>
          <p className="text-sm text-gray-600">
            {t('projectDirectory.modals.detachConfirm.message')?.replace('{name}', entry?.company_name)}
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              {t('projectDirectory.buttons.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={detaching}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {detaching && <Icon name="Loader2" size={14} className="animate-spin" />}
              {t('projectDirectory.buttons.detach')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Row Dropdown ──────────────────────────────────────────────────────────────
const RowDropdown = ({ entry, onEdit, onDetach, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="bg-transparent outline-none focus:outline-none focus:ring-2 focus:ring-primary/20 p-1 text-gray-500 hover:text-primary hover:bg-gray-50 rounded-full transition-colors leading-none"
      >
        ⋮
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-sm py-1 text-sm z-50 min-w-[120px]">
          <button
            onClick={() => { setOpen(false); onEdit(entry); }}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
          >
            {t('projectDirectory.buttons.edit')}
          </button>
          <button
            onClick={() => { setOpen(false); onDetach(entry); }}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
          >
            {t('projectDirectory.buttons.detach')}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ProjectDirectoryPage = () => {
  const { id: projectId } = useParams();
  const { t } = useTranslation();
  const { userProfile } = useAuth();

  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('subcontractor');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'company_name', direction: 'asc' });

  const [showAttachModal, setShowAttachModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [detachingEntry, setDetachingEntry] = useState(null);

  const [toast, setToast] = useState(null);
  const [tabCounts, setTabCounts] = useState({ subcontractor: 0, client: 0, professional: 0 });

  const canEdit = canEditProject(project, userProfile);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    if (projectId) {
      projectService?.getById(projectId)?.then(setProject)?.catch(console.error);
    }
  }, [projectId]);

  // Fetch tab counts independently — one query per linking table
  const refreshTabCounts = async () => {
    if (!projectId) return;
    const counts = await fetchTabCounts(projectId);
    setTabCounts(counts);
  };

  useEffect(() => {
    if (projectId) refreshTabCounts();
  }, [projectId]);

  useEffect(() => {
    fetchRecords();
    setSearchQuery('');
    setFilterCategory('');
    setSortConfig({ key: 'company_name', direction: 'asc' });
  }, [activeTab, projectId]);

  const fetchRecords = async () => {
    setLoading(true);
    let data = [];
    if (activeTab === 'subcontractor') data = await listSubcontractorsForProject(projectId);
    else if (activeTab === 'professional') data = await listProfessionalsForProject(projectId);
    else data = await listClientsForProject(projectId);
    setRecords(data || []);
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtered records
  const filteredRecords = records?.filter((r) => {
    const q = searchQuery?.toLowerCase();
    const matchesSearch = !q ||
      r?.company_name?.toLowerCase()?.includes(q) ||
      r?.contact_person?.toLowerCase()?.includes(q) ||
      r?.contact_email?.toLowerCase()?.includes(q) ||
      r?.contact_phone?.includes(q);

    const matchesFilter = !filterCategory ||
      (activeTab === 'subcontractor' && r?.trade === filterCategory) ||
      (activeTab === 'professional' && r?.profession === filterCategory);

    return matchesSearch && matchesFilter;
  });

  // Sorted records — same logic as company-wide directory
  const sortedRecords = [...filteredRecords]?.sort((a, b) => {
    let aValue = (a?.[sortConfig?.key] || '')?.toString()?.toLowerCase();
    let bValue = (b?.[sortConfig?.key] || '')?.toString()?.toLowerCase();
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterCategory('');
  };

  const getEmptyMessage = () => {
    if (activeTab === 'subcontractor') return t('projectDirectory.empty.subcontractors');
    if (activeTab === 'professional') return t('projectDirectory.empty.professionals');
    return t('projectDirectory.empty.clients');
  };

  const tabs = [
    { key: 'subcontractor', label: t('projectDirectory.tabs.subcontractors') },
    { key: 'client', label: t('projectDirectory.tabs.clients') },
    { key: 'professional', label: t('projectDirectory.tabs.professionals') },
  ];

  const handleAttachSaved = () => {
    setShowAttachModal(false);
    fetchRecords();
    refreshTabCounts();
    showToast(t('projectDirectory.toast.attached'));
  };

  const handleEditSaved = () => {
    setEditingEntry(null);
    fetchRecords();
    showToast(t('projectDirectory.toast.updated'));
  };

  const handleDetachConfirmed = () => {
    setDetachingEntry(null);
    fetchRecords();
    refreshTabCounts();
    showToast(t('projectDirectory.toast.detached'));
  };

  // Get the filter dropdown options from constants (same as company-wide)
  const getFilterOptions = () => {
    if (activeTab === 'subcontractor') return TRADES;
    if (activeTab === 'professional') return PROFESSIONS;
    return null;
  };

  const getFilterPlaceholder = () => {
    if (activeTab === 'subcontractor') return t('projectDirectory.filterAllTrades');
    if (activeTab === 'professional') return t('projectDirectory.filterAllProfessions');
    return null;
  };

  if (!projectId || projectId === ':id') {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-gray-500 text-sm">{t('projectDirectory.errors.noProjectId')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-full" onClick={() => {}}>
      {/* Page Header — matches Aperçu financier style */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Users" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">
                {t('projectDirectory.title')}
              </h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm text-primary font-normal">
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span className="text-sm text-primary font-normal">
                {project?.name || t('common.loading')}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {t('projectDirectory.subtitle')}
            </p>
          </div>

          {canEdit && (
            <div className="flex-shrink-0 flex items-center">
              <button
                onClick={() => setShowAttachModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-[#FF5A1A] rounded-lg transition-colors shadow-sm"
              >
                <Icon name="Plus" size={13} />
                {t('projectDirectory.buttons.add')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar — same layout as company-wide directory */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Tab Toggle — same segmented style as company-wide */}
        <div className="flex items-center h-8 bg-white rounded-lg border border-gray-200 px-1 shadow-sm flex-shrink-0">
          {tabs?.map((tab) => {
            const count = activeTab === tab?.key ? filteredRecords?.length : tabCounts?.[tab?.key] ?? 0;
            return (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className={`h-6 px-3 text-xs font-medium rounded-md transition-all duration-200 flex items-center gap-1.5 ${
                  activeTab === tab?.key
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab?.label}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    activeTab === tab?.key
                      ? 'bg-white/20 text-white' :'bg-gray-100 text-gray-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Vertical Divider */}
        <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

        {/* Search Input — same as company-wide */}
        <input
          type="search"
          placeholder={t('projectDirectory.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="h-8 flex-1 min-w-[180px] rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors shadow-sm"
        />

        {/* Trade / Profession Filter Dropdown — same as company-wide */}
        {getFilterOptions() && (
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e?.target?.value)}
            className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors shadow-sm flex-shrink-0 cursor-pointer"
          >
            <option value="">{getFilterPlaceholder()}</option>
            {getFilterOptions()?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )}

        {/* Clear Filters Button — same as company-wide */}
        <button
          onClick={handleClearFilters}
          className="h-8 inline-flex items-center gap-1.5 px-2.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
        >
          <Icon name="X" size={13} />
          {t('directory.filter.clearFilters')}
        </button>
      </div>

      {/* Table — card-row layout identical to company-wide directory */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : sortedRecords?.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-[#6B7280]" />
          <p className="text-[#6B7280] mb-3">{getEmptyMessage()}</p>
          {canEdit && (
            <button
              onClick={() => setShowAttachModal(true)}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1 mx-auto"
            >
              <Icon name="Plus" size={14} />
              {t('projectDirectory.buttons.add')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Table Header — sortable, same as company-wide */}
          <div className="flex items-center h-12">
            {/* Nom de l'entreprise */}
            <div
              className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
              style={{ minWidth: '200px', flex: 1 }}
              onClick={() => handleSort('company_name')}
            >
              <span>{t('directory.table.companyName')}</span>
              <SortIcon columnKey="company_name" sortConfig={sortConfig} />
            </div>

            {/* Corps de métier — subcontractors only */}
            {activeTab === 'subcontractor' && (
              <div
                className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                style={{ width: '160px', flexShrink: 0 }}
                onClick={() => handleSort('trade')}
              >
                <span>{t('directory.table.trade')}</span>
                <SortIcon columnKey="trade" sortConfig={sortConfig} />
              </div>
            )}

            {/* Profession — professionals only */}
            {activeTab === 'professional' && (
              <div
                className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
                style={{ width: '170px', flexShrink: 0 }}
                onClick={() => handleSort('profession')}
              >
                <span>{t('directory.table.profession')}</span>
                <SortIcon columnKey="profession" sortConfig={sortConfig} />
              </div>
            )}

            {/* Personne-ressource */}
            <div
              className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
              style={{ width: '170px', flexShrink: 0 }}
              onClick={() => handleSort('contact_person')}
            >
              <span>{t('directory.table.contactPerson')}</span>
              <SortIcon columnKey="contact_person" sortConfig={sortConfig} />
            </div>

            {/* Téléphone */}
            <div
              className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
              style={{ width: '150px', flexShrink: 0 }}
              onClick={() => handleSort('contact_phone')}
            >
              <span>{t('directory.table.phone')}</span>
              <SortIcon columnKey="contact_phone" sortConfig={sortConfig} />
            </div>

            {/* Courriel */}
            <div
              className="px-6 text-xs font-semibold text-foreground/80 flex items-center gap-2 cursor-pointer hover:bg-muted/30 transition-colors"
              style={{ width: '200px', flexShrink: 0 }}
              onClick={() => handleSort('contact_email')}
            >
              <span>{t('directory.table.email')}</span>
              <SortIcon columnKey="contact_email" sortConfig={sortConfig} />
            </div>

            {/* Évaluation — subcontractors only */}
            {activeTab === 'subcontractor' && (
              <div
                className="px-6 text-xs font-semibold text-foreground/80"
                style={{ width: '130px', flexShrink: 0 }}
              >
                <span>{t('directory.table.rating')}</span>
              </div>
            )}

            {/* Actions */}
            {canEdit && (
              <div className="px-6 text-xs font-semibold text-foreground/80 text-right" style={{ width: '90px', flexShrink: 0 }}>
                <span>{t('directory.table.actions')}</span>
              </div>
            )}
          </div>

          {/* Row Cards — identical styling to company-wide directory */}
          {sortedRecords?.map((entry) => (
            <div
              key={entry?.projectEntryId}
              className="flex items-center h-10 bg-white border border-gray-200 rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.02)] transition-all duration-200 hover:bg-gray-100/60 hover:border-primary"
            >
              {/* Nom de l'entreprise */}
              <div className="px-6 text-xs leading-none text-foreground min-w-0 truncate whitespace-nowrap overflow-hidden text-ellipsis" style={{ minWidth: '200px', flex: 1 }}>
                <span className="font-medium">{entry?.company_name}</span>
                {!entry?.active && (
                  <span className="ml-2 text-[10px] text-gray-400 italic">{t('projectDirectory.inactive')}</span>
                )}
              </div>

              {/* Corps de métier pill — subcontractors, same orange pill as company-wide */}
              {activeTab === 'subcontractor' && (
                <div className="px-6" style={{ width: '160px', flexShrink: 0 }}>
                  {entry?.trade ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {entry?.trade}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </div>
              )}

              {/* Profession pill — professionals, same blue pill as company-wide */}
              {activeTab === 'professional' && (
                <div className="px-6" style={{ width: '170px', flexShrink: 0 }}>
                  {entry?.profession ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                      {entry?.profession}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </div>
              )}

              {/* Personne-ressource */}
              <div className="px-6 text-xs leading-none text-foreground" style={{ width: '170px', flexShrink: 0 }}>
                {entry?.contact_person || '-'}
              </div>

              {/* Téléphone */}
              <div className="px-6 text-xs leading-none text-foreground" style={{ width: '150px', flexShrink: 0 }}>
                {entry?.contact_phone || '-'}
              </div>

              {/* Courriel */}
              <div className="px-6 text-xs leading-none text-foreground truncate" style={{ width: '200px', flexShrink: 0 }}>
                {entry?.contact_email ? (
                  <a href={`mailto:${entry?.contact_email}`} className="hover:text-primary transition-colors truncate block">
                    {entry?.contact_email}
                  </a>
                ) : '-'}
              </div>

              {/* Évaluation — subcontractors only, same StarRating as company-wide */}
              {activeTab === 'subcontractor' && (
                <div className="px-6" style={{ width: '130px', flexShrink: 0 }}>
                  <StarRating
                    value={entry?.rating ?? null}
                    onChange={() => {}}
                    size="sm"
                  />
                </div>
              )}

              {/* Actions */}
              {canEdit && (
                <div className="px-6 text-right relative" style={{ width: '90px', flexShrink: 0 }}>
                  <RowDropdown
                    entry={entry}
                    onEdit={setEditingEntry}
                    onDetach={setDetachingEntry}
                    t={t}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showAttachModal && (
        <AttachModal
          activeTab={activeTab}
          projectId={projectId}
          onClose={() => setShowAttachModal(false)}
          onSaved={handleAttachSaved}
          t={t}
        />
      )}
      {editingEntry && (
        <EditModal
          entry={editingEntry}
          activeTab={activeTab}
          onClose={() => setEditingEntry(null)}
          onSaved={handleEditSaved}
          t={t}
        />
      )}
      {detachingEntry && (
        <DetachModal
          entry={detachingEntry}
          activeTab={activeTab}
          onClose={() => setDetachingEntry(null)}
          onConfirmed={handleDetachConfirmed}
          t={t}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ProjectDirectoryPage;
